import * as React from 'react';
import { useMemo } from 'react';

import { Select, Tag } from 'antd';
import { useAtomValue } from 'jotai';
import { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { Reservation } from '../store/reservation';
import { allUsersAtom } from '../store/user';
import { UserLabel } from './User';
import { FormattedMessage, useIntl } from 'react-intl';
import { focusResaAtom } from '../store/core';

export const ParticipantsSelect = (props: {
	users: Reservation['users'];
	onChange?: (users: Reservation['users']) => void;
}) => {
	const allUsers = useAtomValue(allUsersAtom);
	const resolvedUsers = useMemo(() => {
		return props.users
			.map((resaUser) => {
				const user = allUsers.find((u) => u.ID == resaUser.userId);
				if (!user) return false;
				return {
					...resaUser,
					value: user.ID,
					label: <UserLabel user={user} accepted={resaUser.accepted} />,
					user,
				};
			})
			.filter((x) => !!x);
	}, [props.users, allUsers]);
	const accepted = useMemo(() => {
		return props.users
			.map((u) => u.accepted && u.userId)
			.filter((x): x is number => !!x);
	}, [props.users]);

	const loading = props.users.length > resolvedUsers.length;

	const { isAdmin, loggedInUser } = useAtomValue(focusResaAtom);
	const availableUsers = allUsers.filter(
		(u) => isAdmin || !u.roles.includes('um_presta') || u.ID === loggedInUser,
	);

	return (
		<Select
			value={resolvedUsers}
			labelInValue
			mode="multiple"
			loading={loading}
			onChange={(_, o) =>
				props.onChange?.(
					(Array.isArray(o) ? o : [o])
						.filter((x) => !!x.user)
						.map((u) => {
							return {
								userId: u.user.ID,
								accepted: accepted.includes(u.user.ID),
							};
						}),
				)
			}
			placeholder={
				loading ? (
					<FormattedMessage
						defaultMessage="Chargement des participants..."
						id="+cC7RV"
					/>
				) : (
					<FormattedMessage
						defaultMessage="Inviter des participants"
						id="nfddDq"
					/>
				)
			}
			options={availableUsers.map((user) => ({
				label: <UserLabel user={user} accepted={accepted.includes(user.ID)} />,
				value: user.ID,
				user,
			}))}
			filterOption={(input, o) =>
				o?.user.display_name
					?.toLocaleLowerCase()
					.includes(input.toLocaleLowerCase()) ?? false
			}
			tagRender={(props) => (
				<ParticipantTag
					{...props}
					accepted={accepted}
					onClick={(id) => {
						const url = resolvedUsers.find((u) => u.userId == id)?.user.link;
						url && window.open(url, '_blank');
					}}
				/>
			)}
		/>
	);
};

const ParticipantTag = (
	props: CustomTagProps & {
		accepted: number[];
		onClick: (userId: any) => void;
	},
) => {
	const accepted = props.accepted.includes(props.value);
	const intl = useIntl();
	return (
		<Tag
			closable={props.closable && !accepted}
			onClose={props.onClose}
			color={accepted ? '#f05e39' : '#A5A6B4'}
			title={
				accepted
					? intl.formatMessage({
							id: 'xS1WDH',
							defaultMessage: "Ce participant a accepté l'invitation.",
						})
					: intl.formatMessage({
							id: 'rZT2Ql',
							defaultMessage:
								"Ce participant n'a pas encore accepté l'invitation.",
						})
			}
		>
			{React.cloneElement(props.label as React.ReactElement, {
				clickable: true,
			})}
		</Tag>
	);
};
